import { __awaiter, __generator, __assign } from '../node_modules/tslib/tslib.es6.js';
import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import { customAlphabet } from 'nanoid';
import React, { useEffect } from 'react';
import { Cookies } from 'react-cookie';
import { create } from 'zustand';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils.js';
import { track } from './AnalyticsService.js';
import { AnalyticsTrackingEvent } from './events.js';
import { timeoutPromise } from './utils.js';

var useFingerprintJSStore = create(function (set) { return ({
    visitorId: undefined,
    setVisitorTracking: function (visitorTracking) {
        var _a;
        return set({
            visitorId: (_a = visitorTracking.uniqueVisitorId) !== null && _a !== void 0 ? _a : undefined,
            visitorSessionId: visitorTracking.visitorSessionId,
            visitorTracking: visitorTracking,
        });
    },
    visitorTracking: undefined,
    fpLoadPromise: undefined,
    setFpLoadPromise: function (fpLoadPromise) { return set({ fpLoadPromise: fpLoadPromise }); },
}); });
var nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 10);
var FINGERPRINT_JS_BROWSER_API_KEY = 'K1ACT61coP6ep24p7Bwr';
// const FINGERPRINT_JS_SERVER_API_KEY = "***HAVE_TO_GET_IT***";
var FingerprintJSContext = React.createContext(undefined);
var useFingerprintJSContext = function () { return React.useContext(FingerprintJSContext); };
var DEBUG = false;
var vsCookieName = '_vs_id';
var fpResultCacheKey = "fp:".concat(vsCookieName, ":result");
var cookies = new Cookies();
var visitorSessionId = cookies.get(vsCookieName);
if (!visitorSessionId) {
    visitorSessionId = nanoid();
    cookies.set(vsCookieName, visitorSessionId, {
        domain: '.' + BrandclubStoreConfigUtils.getBaseDomain(),
        maxAge: 60 * 60 * 24,
        path: '/',
        secure: true,
        httpOnly: false,
        sameSite: 'lax',
    });
}
var FingerprintJSProvider = function (_a) {
    var children = _a.children, hostDomainName = _a.hostDomainName;
    var FINGERPRINT_JS_AGENT_ENDPOINT = hostDomainName;
    var _b = useFingerprintJSStore(function (state) { return [
        state.visitorTracking,
        state.setVisitorTracking,
        state.fpLoadPromise,
        state.setFpLoadPromise,
    ]; }), visitorTracking = _b[0], setVisitorTracking = _b[1], fpLoadPromise = _b[2], setFpLoadPromise = _b[3];
    useEffect(function () {
        var referrer = document.referrer;
        if (!(visitorTracking === null || visitorTracking === void 0 ? void 0 : visitorTracking.uniqueVisitorId) || !(visitorTracking === null || visitorTracking === void 0 ? void 0 : visitorTracking.isResolved)) {
            var fpResultValue = window.localStorage.getItem(fpResultCacheKey);
            if (!fpResultValue) {
                // Try to get from sessionStorage if not in localStorage
                fpResultValue = window.sessionStorage.getItem(fpResultCacheKey);
                if (fpResultValue) {
                    // Cache in localStorage for future visits
                    window.localStorage.setItem(fpResultCacheKey, fpResultValue);
                    window.sessionStorage.removeItem(fpResultCacheKey); // Remove from sessionStorage
                }
            }
            if (fpResultValue) {
                try {
                    var _a = JSON.parse(fpResultValue), cachedSessionId = _a.visitorSessionId, result = _a.result;
                    // Check if the cached result is for the current session
                    if (visitorSessionId === cachedSessionId) {
                        setVisitorTracking({
                            visitorSessionId: visitorSessionId,
                            uniqueVisitorId: result.visitorId,
                            referrer: referrer,
                            fingerprintResult: result,
                            isResolved: true,
                        });
                        // eslint-disable-next-line no-console
                        DEBUG && console.log('FingerprintJS loaded from cache', visitorSessionId);
                        return;
                    }
                }
                catch (e) {
                    console.error("Error parsing cached fingerprint result: ".concat(e));
                }
            }
            if (fpLoadPromise) {
                return;
            }
            var fpLoadPromiseBuilder = function () {
                var isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator === null || navigator === void 0 ? void 0 : navigator.userAgent);
                if (isBot) {
                    var result = {
                        visitorId: 'bc-bot:' + visitorSessionId,
                        bot: true,
                    };
                    setVisitorTracking({
                        visitorSessionId: visitorSessionId,
                        uniqueVisitorId: result.visitorId,
                        referrer: referrer,
                        fingerprintResult: result,
                        isResolved: true,
                    });
                    window.localStorage.setItem(fpResultCacheKey, JSON.stringify({ visitorSessionId: visitorSessionId, result: result }));
                    setFpLoadPromise(undefined);
                    return Promise.resolve();
                }
                return timeoutPromise(2e3, 'FJs.load', function () {
                    return FingerprintJS.load({
                        scriptUrlPattern: 
                        // "https://fpnpmcdn.net/v<version>/<apiKey>/loader_v<loaderVersion>.js",
                        "".concat(FINGERPRINT_JS_AGENT_ENDPOINT, "/Jzq31cA2yOXuX74326KI33y/dXtUA01lo82H22P4lNPkV46S?apiKey=<apiKey>&version=3&loaderVersion=3.8.1"),
                        apiKey: FINGERPRINT_JS_BROWSER_API_KEY,
                        // This works but goes through our proxy but FINGERPRINT_JS_BROWSER_ENDPOINT directly works as well
                        endpoint: "".concat(FINGERPRINT_JS_AGENT_ENDPOINT, "/Jzq31cA2yOXuX74326KI33y/5ksfEIl7Q0t2f8es3L1oIS99")
                            ,
                    });
                })
                    .then(function (fp) {
                    return fp
                        .get({ extendedResult: true })
                        .then(function (result) {
                        // console.log('Fjs result', result);
                        setVisitorTracking({
                            visitorSessionId: visitorSessionId,
                            uniqueVisitorId: result.visitorId,
                            referrer: referrer,
                            fingerprintResult: result,
                            isResolved: true,
                        });
                        window.localStorage.setItem(fpResultCacheKey, JSON.stringify({ visitorSessionId: visitorSessionId, result: result }));
                    })
                        .catch(function (e) {
                        setVisitorTracking({
                            visitorSessionId: visitorSessionId,
                            uniqueVisitorId: 'bc-session:' + visitorSessionId,
                            referrer: referrer,
                            isResolved: false,
                        });
                        track(AnalyticsTrackingEvent.FINGERPRINTJS_ERROR, {
                            error: e,
                            type: 'get',
                        });
                    });
                })
                    .catch(function (e) {
                    setVisitorTracking({
                        visitorSessionId: visitorSessionId,
                        uniqueVisitorId: 'bc-session:' + visitorSessionId,
                        referrer: referrer,
                        isResolved: false,
                    });
                    track(AnalyticsTrackingEvent.FINGERPRINTJS_ERROR, {
                        type: 'load',
                        error: e,
                    });
                })
                    .finally(function () {
                    setFpLoadPromise(undefined);
                });
            };
            setFpLoadPromise(fpLoadPromiseBuilder());
        }
    }, [visitorTracking]);
    if (!visitorTracking) {
        return null;
    }
    return React.createElement(FingerprintJSContext.Provider, { value: visitorTracking }, children);
};
var useVisitorTracking = function () {
    var state = useFingerprintJSStore.getState();
    return [state.visitorTracking, state.setVisitorTracking];
};
/**
 * Visitor ID is not immediately available and sometimes returns an empty string.
 * This function will keep checking for the visitorId until it is available or until a timeout is reached (10 seconds).
 * @returns Promise<string> - A promise that resolves to the visitorId
 */
var USER_ID_FETCH_TIMEOUT = 3000;
var asyncGetVisitorId = function () {
    var elapsed = Date.now();
    return new Promise(function (resolve, reject) {
        var checkTrackingInfo = function () {
            var _a = useVisitorTracking(), visitorTracking = _a[0], setVisitorTracking = _a[1];
            if (visitorTracking === null || visitorTracking === void 0 ? void 0 : visitorTracking.uniqueVisitorId) {
                resolve(visitorTracking.uniqueVisitorId);
                if (!visitorTracking.isResolved) {
                    // to re-render the component which initiates the fingerprintjs load again
                    setVisitorTracking(__assign({}, visitorTracking));
                }
                return;
            }
            if (Date.now() - elapsed > USER_ID_FETCH_TIMEOUT) {
                reject('Timeout waiting for visitorId');
                return;
            }
            setTimeout(checkTrackingInfo, 100);
        };
        checkTrackingInfo();
    });
};
/**
 * Tries to get the visitorId and returns undefined if it fails.
 * @returns Promise<string | undefined> - A promise that resolves to the visitorId or undefined if it fails
 */
var tryGetVisitorId = function () { return __awaiter(void 0, void 0, void 0, function () {
    var e_1;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                _a.trys.push([0, 2, , 3]);
                return [4 /*yield*/, asyncGetVisitorId()];
            case 1: return [2 /*return*/, _a.sent()];
            case 2:
                e_1 = _a.sent();
                console.error(e_1);
                return [2 /*return*/, undefined];
            case 3: return [2 /*return*/];
        }
    });
}); };

export { FingerprintJSContext, FingerprintJSProvider, tryGetVisitorId, useFingerprintJSContext };
//# sourceMappingURL=fingerprint.js.map
